/*  eslint-disable react/display-name */
/* eslint-disable react/no-multi-comp */
import React, { useContext, useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportsContext, type FormValues } from './ReportsContext';
import { fetchPosts } from './fetchPosts';
import { Form, useFormikContext } from 'formik';
import {
    Button as AntButton,
    Flex,
    Radio,
    Typography,
    DatePicker,
    Select,
    Checkbox,
    TreeSelect,
    Drawer,
    type CheckboxProps,
} from '@app/ui';
import './ReportsConfig.scss';
import dayjs from 'dayjs';
import ReportCoverPicker from './ReportCoverPicker';
import hasRequiredSelection from './hasRequiredSelection';
const { RangePicker } = DatePicker;
const { SHOW_PARENT } = TreeSelect;

const dropdownWithSelectAll =
    (selectAll, hasOptions, selectAllText: string) => menu => (
        <>
            {hasOptions && (
                <div
                    style={{ padding: '8px 12px', cursor: 'pointer' }}
                    onClick={selectAll}
                >
                    <Typography.Text strong>{selectAllText}</Typography.Text>
                </div>
            )}
            {menu}
        </>
    );

const ReportsConfigFilters = () => {
    const { t } = useTranslation('tools');
    const abortControllerRef = useRef(null);
    const wasAbortedRef = useRef(false);
    const { values, resetForm, submitForm } = useFormikContext<FormValues>();

    const reportOptions = useMemo(
        () => [
            { value: 'standard', label: t('reports.filters.options.standard') },
            { value: 'pacing', label: t('reports.filters.options.pacingGoal') },
        ],
        [t],
    );

    const dateOptions = useMemo(
        () => [
            { value: 'all', label: t('reports.filters.options.allTime') },
            {
                value: 'specific',
                label: t('reports.filters.options.customRange'),
            },
        ],
        [t],
    );

    const placementRadioOptions = useMemo(
        () => [
            { value: 'all', label: t('reports.filters.options.allPlacements') },
            {
                value: 'single',
                label: t('reports.filters.options.singlePlacement'),
            },
        ],
        [t],
    );
    const [fetchedPosts, setFetchedPosts] = useState([]);
    const [isScheduleMode, setIsScheduleMode] = useState(false);
    const {
        setFormValue,
        goalsOptions,
        shouldShowLeagueHierarchy,
        isLoadingFilters,
        rhTreeData,
        rightsholdersOptions,
        sponsorOptions,
        selectedRightsholders,
        placementOptions,
        setSearchDone,
        availableMedia,
        setAvailableMedia,
        filteredPlacements,
        setIsLoadingPosts,
        isLoadingPosts,
        sendNotification,
        clearAllNotifs,
        searchDone,
        isDrawerOpen,
        showDrawer,
        closeDrawer,
        onChangeRightsholders,
        openScheduleModalForCreate,
        isCreateMode,
        client,
        rightsholderAttribution,
        hasReportsBroadcastCountryFilter,
        broadcastCountryOptions,
        normalizedBroadcastCountries,
    } = useContext(ReportsContext);

    // Reset isScheduleMode when the modal is closed
    useEffect(() => {
        if (!isCreateMode && isScheduleMode) {
            setIsScheduleMode(false);
        }
    }, [isCreateMode, isScheduleMode]);

    const hasNoReportableData =
        searchDone &&
        !isLoadingPosts &&
        !wasAbortedRef.current &&
        !availableMedia.image &&
        !availableMedia.video &&
        !availableMedia.broadcast;

    const hasOnlyVideos =
        availableMedia &&
        !availableMedia.image &&
        (availableMedia.video || availableMedia.broadcast);

    const onDateChange = dates => {
        if (dates) {
            setFormValue('startDate')(dates[0]);
            setFormValue('endDate')(dates[1]);
        } else {
            setFormValue('startDate')(null);
            setFormValue('endDate')(null);
        }
    };

    const onReportTypeChange = e => {
        const reportType = e.target.value;
        setFormValue('reportType')(reportType);
        if (reportType !== 'pacing') {
            setFormValue('goal')(null);
            setFormValue('selectedTreeRightsholders')([]);
            setFormValue('rightsholders')([]);
            setFormValue('sponsors')([]);
            setFormValue('placement')(null);
            setFormValue('placementSelectionType')('all');
            setFormValue('startDate')(null);
            setFormValue('endDate')(null);
        } else {
            // Reset selected rightsholders, sponsors and placements when switching to pacing
            setFormValue('selectedTreeRightsholders')([]);
            setFormValue('rightsholders')([]);
            setFormValue('sponsors')([]);
            setFormValue('placement')(null);
            setFormValue('placementSelectionType')('all');
            setFormValue('startDate')(null);
            setFormValue('endDate')(null);
        }
    };

    const onDateSelectionTypeChange = e => {
        const dateSelectionType = e.target.value;
        setFormValue('dateSelectionType')(dateSelectionType);
        if (dateSelectionType !== 'specific') {
            setFormValue('startDate')(null);
            setFormValue('endDate')(null);
        }
    };

    const onPlacementSelectionTypeChange = e => {
        const placementSelectionType = e.target.value;
        setFormValue('placementSelectionType')(placementSelectionType);
        setFormValue('placement')(null);
    };

    const onChangeMethodologyFrames: CheckboxProps['onChange'] = e => {
        const includesMethodologySlides = e.target.checked;
        setFormValue('includesMethodologySlides')(includesMethodologySlides);
    };

    const onChangeBroadcastCountries = (countryIds: number[]) => {
        setFormValue('broadcastCountryIds')(countryIds);
        setFormValue('broadcastCountryNames')(
            countryIds
                .map(
                    countryId =>
                        broadcastCountryOptions.find(
                            ({ value }) => value === countryId,
                        )?.label,
                )
                .filter(Boolean),
        );
    };

    const canSubmit = useMemo(() => {
        if (!hasRequiredSelection(values)) {
            return false;
        }

        if (values.reportType === 'pacing') {
            return true;
        }

        const hasCustomDate =
            values.dateSelectionType !== 'specific' ||
            (values.startDate && values.endDate);
        const hasPlacement =
            values.placementSelectionType !== 'single' ||
            values.placement !== null;

        return hasCustomDate && hasPlacement;
    }, [
        values.reportType,
        values.goal,
        values.dateSelectionType,
        values.startDate,
        values.endDate,
        values.rightsholders,
        values.sponsors,
        values.placementSelectionType,
        values.placement,
    ]);

    const handleReportSubmit =
        (isSchedule = false) =>
        async () => {
            wasAbortedRef.current = false;
            setSearchDone(false);
            setIsLoadingPosts(true);

            const controller = new AbortController();
            abortControllerRef.current = controller;

            const rhWithSP = values.rightsholders;
            const sponsors = values.sponsors?.map(String) ?? [];
            const filteredIds = filteredPlacements;

            const rightsholders = selectedRightsholders.reduce((acc, id) => {
                acc[id] = true;
                return acc;
            }, {});

            const startDate = values.startDate?.toDate() ?? null;
            const endDate = values.endDate?.toDate() ?? null;

            const { posts, availableMedia } = await fetchPosts({
                ...values,
                startDate,
                endDate,
                sponsors,
                rhWithSP,
                rightsholders,
                filteredPlacements: filteredIds,
                client,
                rightsholderAttribution,
                broadcastNetworkCountryIds:
                    normalizedBroadcastCountries.broadcastCountryIds,
                onTimeout: () => {
                    sendNotification(t('reports.messages.error.tooMuchData'), {
                        context: 'danger',
                    });
                },
                defaultOnError: msg =>
                    sendNotification(msg, { context: 'danger' }),
                setIsLoadingPosts,
                clearAllNotifs,
                onStartFetchingPosts: () => {
                    sendNotification(
                        t('reports.messages.loading.findingData'),
                        {
                            type: 'notification',
                            context: 'info',
                            hasActionButton: true,
                            onCancelActionButton: () => {
                                wasAbortedRef.current = true;
                                controller.abort();
                            },
                        },
                        false,
                        true,
                    );
                },
                signal: controller.signal,
            });

            setFetchedPosts(posts);
            setAvailableMedia(availableMedia);
            setSearchDone(true);

            const hasSocial = availableMedia?.image || availableMedia?.video;
            const hasBroadcast = availableMedia?.broadcast;

            if (!hasSocial && !hasBroadcast) return;

            if (isSchedule) {
                setIsScheduleMode(true);
                openScheduleModalForCreate();
                return;
            }

            showDrawer();
        };

    const onClose = () => {
        closeDrawer(false);
        setIsScheduleMode(false);
    };

    return (
        <Form>
            <Flex className="form-wrapper">
                <Flex className="input-container">
                    <Flex className="form-item two-thirds">
                        <Flex className="form-label-group">
                            <Typography.Text className="form-label">
                                {t('reports.filters.reportType')}
                            </Typography.Text>
                            <Radio.Group
                                block
                                options={reportOptions}
                                value={values.reportType}
                                className="-m-l-2"
                                onChange={onReportTypeChange}
                                style={{ width: '280px', whiteSpace: 'nowrap' }}
                                disabled={isLoadingPosts}
                            />
                        </Flex>
                        <Select
                            placeholder={t('reports.filters.selectGoal')}
                            autoClearSearchValue={false}
                            options={goalsOptions}
                            value={values.goal || []}
                            onChange={setFormValue('goal')}
                            optionFilterProp="label"
                            allowClear
                            style={{ width: '100%' }}
                            disabled={
                                values.reportType !== 'pacing' ||
                                !goalsOptions.length ||
                                isLoadingPosts
                            }
                        />
                    </Flex>

                    <Flex className="form-item one-third">
                        <Flex className="form-label-group">
                            <Typography.Text className="form-label">
                                {t('reports.filters.dateRange')}
                            </Typography.Text>
                            <Radio.Group
                                block
                                options={dateOptions}
                                value={values.dateSelectionType}
                                className="-m-l-2"
                                onChange={onDateSelectionTypeChange}
                                style={{ width: '280px', whiteSpace: 'nowrap' }}
                                disabled={
                                    values.reportType === 'pacing' ||
                                    isLoadingPosts
                                }
                            />
                        </Flex>
                        <RangePicker
                            value={[values.startDate, values.endDate]}
                            onChange={onDateChange}
                            maxDate={dayjs()}
                            disabled={
                                values.dateSelectionType !== 'specific' ||
                                values.reportType === 'pacing' ||
                                isLoadingPosts
                            }
                            style={{ width: '100%' }}
                        />
                    </Flex>
                </Flex>
                <Flex className="input-container">
                    <Flex className="form-item one-fourth">
                        <Flex className="form-label-group">
                            <Typography.Text className="form-label">
                                {t('reports.filters.rightsholders')}
                            </Typography.Text>
                        </Flex>
                        {!shouldShowLeagueHierarchy ? (
                            <Select
                                mode="multiple"
                                placeholder={t(
                                    'reports.filters.selectRightsholders',
                                )}
                                options={rightsholdersOptions}
                                value={values.selectedTreeRightsholders}
                                onChange={onChangeRightsholders}
                                optionFilterProp="label"
                                allowClear
                                style={{ width: '100%' }}
                                autoClearSearchValue={false}
                                disabled={
                                    values.reportType === 'pacing' ||
                                    isLoadingPosts
                                }
                            />
                        ) : (
                            <TreeSelect
                                treeCheckable
                                showCheckedStrategy={SHOW_PARENT}
                                placeholder={t('reports.filters.rightsholders')}
                                treeData={rhTreeData}
                                value={values.selectedTreeRightsholders}
                                onChange={onChangeRightsholders}
                                treeNodeFilterProp="title"
                                showSearch
                                autoClearSearchValue={false}
                                allowClear
                                maxTagCount={3}
                                maxTagTextLength={8}
                                maxTagPlaceholder={`...`}
                                dropdownRender={dropdownWithSelectAll(
                                    () => {
                                        onChangeRightsholders(
                                            rhTreeData.map(
                                                ({ value }) => value,
                                            ),
                                        );
                                    },
                                    rhTreeData.length > 0,
                                    t('reports.filters.selectAll'),
                                )}
                                className="-filter-select"
                                disabled={
                                    isLoadingFilters ||
                                    rightsholdersOptions.length === 0 ||
                                    values.reportType === 'pacing' ||
                                    isLoadingPosts
                                }
                                style={{ width: '100%' }}
                            />
                        )}
                    </Flex>

                    <Flex className="form-item one-fourth">
                        <Flex className="form-label-group">
                            <Typography.Text className="form-label">
                                {t('reports.filters.sponsors')}
                            </Typography.Text>
                        </Flex>
                        <Select
                            mode="multiple"
                            placeholder={t('reports.filters.selectSponsors')}
                            options={sponsorOptions}
                            value={values.sponsors}
                            onChange={setFormValue('sponsors')}
                            optionFilterProp="label"
                            allowClear
                            maxTagCount={2}
                            maxTagTextLength={8}
                            maxTagPlaceholder={`...`}
                            style={{ width: '100%' }}
                            autoClearSearchValue={false}
                            disabled={
                                isLoadingFilters ||
                                sponsorOptions.length === 0 ||
                                values.reportType === 'pacing' ||
                                isLoadingPosts
                            }
                            dropdownRender={dropdownWithSelectAll(
                                () => {
                                    setFormValue('sponsors')(
                                        sponsorOptions.map(
                                            ({ value }) => value,
                                        ),
                                    );
                                },
                                sponsorOptions.length > 0,
                                t('reports.filters.selectAll'),
                            )}
                            className="-filter-select"
                        />
                    </Flex>

                    <Flex className="form-item one-fourth">
                        <Flex className="form-label-group">
                            <Typography.Text className="form-label">
                                {t('reports.filters.placement')}
                            </Typography.Text>
                            <Radio.Group
                                block
                                options={placementRadioOptions}
                                value={values.placementSelectionType}
                                className="-m-l-2"
                                onChange={onPlacementSelectionTypeChange}
                                style={{ whiteSpace: 'nowrap' }}
                                disabled={
                                    values.reportType === 'pacing' ||
                                    selectedRightsholders.length !== 1 ||
                                    isLoadingPosts
                                }
                            />
                        </Flex>
                        <Select
                            placeholder={t('reports.filters.selectPlacement')}
                            options={placementOptions}
                            value={values.placement || null}
                            onChange={setFormValue('placement')}
                            optionFilterProp="label"
                            allowClear
                            style={{ width: '100%' }}
                            disabled={
                                values.placementSelectionType !== 'single' ||
                                values.reportType === 'pacing' ||
                                placementOptions.length === 0 ||
                                isLoadingPosts
                            }
                        />
                    </Flex>
                    {hasReportsBroadcastCountryFilter && (
                        <Flex className="form-item one-fourth">
                            <Flex className="form-label-group">
                                <Typography.Text className="form-label">
                                    {t('reports.filters.broadcastCountries')}
                                </Typography.Text>
                            </Flex>
                            <Select
                                mode="multiple"
                                placeholder={t(
                                    'reports.filters.allBroadcastCountries',
                                )}
                                options={broadcastCountryOptions}
                                value={values.broadcastCountryIds}
                                onChange={onChangeBroadcastCountries}
                                optionFilterProp="label"
                                allowClear
                                showSearch
                                maxTagCount={2}
                                maxTagPlaceholder="..."
                                autoClearSearchValue={false}
                                disabled={
                                    isLoadingFilters ||
                                    isLoadingPosts ||
                                    broadcastCountryOptions.length === 0
                                }
                                dropdownRender={dropdownWithSelectAll(
                                    () =>
                                        onChangeBroadcastCountries(
                                            broadcastCountryOptions.map(
                                                ({ value }) => value,
                                            ),
                                        ),
                                    broadcastCountryOptions.length > 0,
                                    t('reports.filters.selectAll'),
                                )}
                                data-test="report-broadcast-country-filter"
                                style={{ width: '100%' }}
                            />
                        </Flex>
                    )}
                </Flex>
                <Flex className="check-container">
                    <Checkbox
                        checked={values.includesMethodologySlides}
                        onChange={onChangeMethodologyFrames}
                        disabled={isLoadingPosts}
                    >
                        {t('reports.filters.includeMethodologySlides')}
                    </Checkbox>
                </Flex>
                <div className="-mvc-divider" />
                <Flex
                    className="action-row"
                    gap="small"
                    wrap
                    justify="space-between"
                    align="flex-end"
                    style={{ width: '100%', paddingTop: 12 }}
                >
                    {/* Left side (status messages or spacer) */}
                    <Flex align="center" style={{ minHeight: 24 }}>
                        {!isLoadingPosts && hasNoReportableData ? (
                            <Typography.Text type="danger">
                                {t('reports.messages.error.noReportableData')}
                            </Typography.Text>
                        ) : (
                            // Always show spacer if not showing the text
                            <div style={{ width: '1px', height: '24px' }} />
                        )}
                    </Flex>

                    {/* Right side (buttons) */}
                    <Flex gap="small" wrap align="flex-end">
                        <AntButton
                            type="primary"
                            color="default"
                            variant="solid"
                            data-test="new-update-filter"
                            onClick={() =>
                                resetForm({
                                    values: {
                                        ...values,
                                        reportType: 'standard',
                                        dateSelectionType: 'all',
                                        placementSelectionType: 'all',
                                        includesMethodologySlides: false,
                                        startDate: null,
                                        endDate: null,
                                        goal: null,
                                        selectedTreeRightsholders: [],
                                        rightsholders: [],
                                        sponsors: [],
                                        placement: null,
                                        filteredPlacements: [],
                                        broadcastCountryIds: [],
                                        broadcastCountryNames: [],
                                    },
                                })
                            }
                            icon={<i className="fa-regular fa-xmark -m-r-2" />}
                            disabled={isLoadingPosts}
                        >
                            {t('reports.buttons.clearAll')}
                        </AntButton>
                        <AntButton
                            color="primary"
                            variant="outlined"
                            data-test="new-update-filter"
                            icon={
                                <i className="fa-regular fa-calendar -m-r-2" />
                            }
                            onClick={handleReportSubmit(true)}
                            disabled={
                                isLoadingFilters || !canSubmit || isLoadingPosts
                            }
                        >
                            {t('reports.buttons.scheduleReport')}
                        </AntButton>
                        <AntButton
                            type="primary"
                            data-test="new-update-filter"
                            icon={
                                <i className="fa-regular fa-file-plus -m-r-2" />
                            }
                            onClick={handleReportSubmit(false)}
                            disabled={
                                isLoadingFilters || !canSubmit || isLoadingPosts
                            }
                        >
                            {t('reports.buttons.generateReport')}
                        </AntButton>
                    </Flex>
                </Flex>
            </Flex>
            <Drawer
                title={t('reports.buttons.cancel')}
                width="65vw"
                zIndex={2100}
                closable={{ 'aria-label': 'Close Button' }}
                onClose={onClose}
                open={isDrawerOpen && !isScheduleMode}
                extra={
                    <AntButton
                        type="primary"
                        icon={<i className="fa-regular fa-file-plus -m-r-2" />}
                        onClick={() => submitForm()}
                        disabled={!canSubmit}
                    >
                        {t('reports.buttons.generateReport')}
                    </AntButton>
                }
            >
                <ReportCoverPicker
                    posts={fetchedPosts}
                    hasOnlyVideos={hasOnlyVideos}
                />
            </Drawer>
        </Form>
    );
};

export default ReportsConfigFilters;
